import AccountCombobox from "@app/components/account-combobox";
import type { AccountOption } from "@app/components/account-combobox";
import StakeholderCombobox from "@app/components/stakeholder-combobox";
import type { StakeholderOption } from "@app/components/stakeholder-combobox";
import { useToast } from "@app/components/toast";
import TxnTablePanel, {
  BulkAction,
  TXN_PANEL_DIALOG_CLASS,
  useTxnSort,
} from "@app/components/txn-table-panel";
import { RECLASSIFY_OPTIONS } from "@app/components/txn-table-panel/use-txn-bulk-actions";
import { useTxnPanelRefresh } from "@app/components/txn-table-panel/use-txn-panel-refresh";
import { getPerspectiveId } from "@app/lib/perspective";
import { environment } from "@app/relay/relay-environment";
import * as Dialog from "@radix-ui/react-dialog";
import { Suspense, useState } from "react";
import { commitMutation, graphql, useLazyLoadQuery } from "react-relay";

import type { pnlCellPanel_Query } from "@app/queries/pnlCellPanel_Query.graphql";
import type { pnlCellPanel_MarkTxnsInternalMutation } from "@app/queries/pnlCellPanel_MarkTxnsInternalMutation.graphql";
import type { pnlCellPanel_AttributeTxnsMutation } from "@app/queries/pnlCellPanel_AttributeTxnsMutation.graphql";
import type {
  pnlCellPanel_ClassifyTxnsMutation,
  TxnClassificationEnum,
} from "@app/queries/pnlCellPanel_ClassifyTxnsMutation.graphql";
import type { pnlCellPanel_SetTxnsNeedsReviewMutation } from "@app/queries/pnlCellPanel_SetTxnsNeedsReviewMutation.graphql";
import type { PnlLineDrillEnum } from "@app/queries/pnlCellPanel_Query.graphql";

// What a clicked P&L cell drills into: a line/subtotal, optionally narrowed to one
// category sub-row and/or one counterparty (the month statement's leaf rows), over
// a date window (a month column, the year-total column, or a /diligence year).
//
// `from` is optional because a BALANCE has no start date: the /diligence debt row
// drills every loan leg from the beginning through the year end, which is what
// makes the list sum to the balance rather than to that year's loan activity.
export type PnlCellScope = {
  line: PnlLineDrillEnum;
  categoryId: string | null;
  counterpartyId?: string | null;
  from?: string; // YYYY-MM-DD; omit for an open start
  to: string; // YYYY-MM-DD
  label: string; // panel header (e.g. "Sales & marketing · Mar 2025")
};

const cellQuery = graphql`
  query pnlCellPanel_Query(
    $line: PnlLineDrillEnum
    $categoryId: ID
    $counterpartyId: ID
    $perspectiveId: ID
    $from: String
    $to: String
    $sort: TxnSortEnum
    $sortDirection: String
  ) {
    transactions(
      pnlLine: $line
      categoryId: $categoryId
      counterpartyId: $counterpartyId
      perspectiveId: $perspectiveId
      from: $from
      to: $to
      sort: $sort
      sortDirection: $sortDirection
    ) {
      totalCount
      totalAmount
      limit
      txns {
        txn {
          id
        }
        md5
        postingDate
        description
        amount
        kind
        accountNumber
        internal
        classification
        expenseNature {
          id
          name
        }
        counterparty {
          name
        }
        category
      }
    }
  }
`;

const markTxnsInternalMutation = graphql`
  mutation pnlCellPanel_MarkTxnsInternalMutation($input: MarkTxnsInternalInputObject!) {
    markTxnsInternal(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

const attributeTxnsMutation = graphql`
  mutation pnlCellPanel_AttributeTxnsMutation($input: AttributeTxnsInputObject!) {
    attributeTxns(input: $input) {
      stakeholder {
        id
        name
      }
      errors {
        message
      }
    }
  }
`;

const classifyTxnsMutation = graphql`
  mutation pnlCellPanel_ClassifyTxnsMutation($input: ClassifyTxnsInputObject!) {
    classifyTxns(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

const setTxnsNeedsReviewMutation = graphql`
  mutation pnlCellPanel_SetTxnsNeedsReviewMutation($input: SetTxnsNeedsReviewInputObject!) {
    setTxnsNeedsReview(input: $input) {
      updatedCount
      errors {
        message
      }
    }
  }
`;

// Reclassify options come from the shared registry (R7 — one canonical set across
// every panel; this panel's former list lacked TAX_PAYMENT).

// The cell drill-down: the transactions that sum to the clicked P&L number,
// reviewable and fixable in place. Every fix re-derives server-side
// (Pipeline::Delta) and refreshes the statement via onChanged.
export default function PnlCellPanel({
  scope,
  accountOptions,
  stakeholderOptions,
  onClose,
  onChanged,
}: {
  scope: PnlCellScope;
  accountOptions: readonly AccountOption[];
  stakeholderOptions: readonly StakeholderOption[];
  onClose: () => void;
  onChanged: () => void;
}) {
  return (
    <Dialog.Root open onOpenChange={(open) => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/20 z-40" />
        <Dialog.Content className={TXN_PANEL_DIALOG_CLASS}>
          <Suspense fallback={<div className="p-6 text-sm text-gray-400">Loading transactions…</div>}>
            <PanelContent
              scope={scope}
              accountOptions={accountOptions}
              stakeholderOptions={stakeholderOptions}
              onClose={onClose}
              onChanged={onChanged}
            />
          </Suspense>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function PanelContent({
  scope,
  accountOptions,
  stakeholderOptions,
  onClose,
  onChanged,
}: {
  scope: PnlCellScope;
  accountOptions: readonly AccountOption[];
  stakeholderOptions: readonly StakeholderOption[];
  onClose: () => void;
  onChanged: () => void;
}) {
  const notify = useToast();
  const { refreshing, refresh, queryOptions } = useTxnPanelRefresh();
  const [busy, setBusy] = useState(false);
  const { sort, direction, onSort, sorting, sortVars } = useTxnSort();

  const data = useLazyLoadQuery<pnlCellPanel_Query>(
    cellQuery,
    {
      line: scope.line,
      categoryId: scope.categoryId,
      counterpartyId: scope.counterpartyId ?? null,
      perspectiveId: getPerspectiveId() ?? undefined,
      from: scope.from ?? null,
      to: scope.to,
      ...sortVars,
    },
    queryOptions
  );
  const rows = data.transactions.txns;

  const afterMutation = (clearSelection: () => void) => {
    clearSelection();
    setBusy(false);
    refresh();
    onChanged();
  };

  const fail = (message: string) => {
    setBusy(false);
    notify({ message, kind: "error" });
  };

  // Change the counterparty (re-attribute) — fixes both who a txn belongs to and,
  // through that counterparty's categorization, which P&L line it lands on.
  const attribute = (txnIds: string[], clearSelection: () => void, choice: { stakeholderId?: string; name?: string }) => {
    setBusy(true);
    commitMutation<pnlCellPanel_AttributeTxnsMutation>(environment, {
      mutation: attributeTxnsMutation,
      variables: {
        input: {
          txnIds,
          stakeholderId: choice.stakeholderId,
          name: choice.name,
          perspectiveId: getPerspectiveId() ?? undefined,
        },
      },
      onCompleted: (res) => {
        const payload = res.attributeTxns;
        if (payload?.errors?.length) return fail(payload.errors[0].message);
        notify({ message: `Set the counterparty on ${txnIds.length} transaction(s).`, kind: "success" });
        afterMutation(clearSelection);
      },
      onError: () => fail("Could not attribute — please try again."),
    });
  };

  // Reclassify the selected txns (loan / dividend / salary / fraudulent / clear).
  const classify = (txnIds: string[], clearSelection: () => void, classification: TxnClassificationEnum | null) => {
    setBusy(true);
    commitMutation<pnlCellPanel_ClassifyTxnsMutation>(environment, {
      mutation: classifyTxnsMutation,
      variables: { input: { txnIds, classification } },
      onCompleted: (res) => {
        const payload = res.classifyTxns;
        if (payload?.errors?.length) return fail(payload.errors[0].message);
        notify({ message: `Reclassified ${txnIds.length} transaction(s).`, kind: "success" });
        afterMutation(clearSelection);
      },
      onError: () => fail("Could not reclassify — please try again."),
    });
  };

  // Not a real operating line: mark the selected txns internal (a transfer between
  // owned accounts), removing them from the corpus.
  const markInternal = (txnIds: string[], clearSelection: () => void, choice: { bankAccountId?: string; name?: string }) => {
    setBusy(true);
    commitMutation<pnlCellPanel_MarkTxnsInternalMutation>(environment, {
      mutation: markTxnsInternalMutation,
      variables: {
        input: { txnIds, destinationBankAccountId: choice.bankAccountId, destinationName: choice.name },
      },
      onCompleted: (res) => {
        const payload = res.markTxnsInternal;
        if (payload?.errors?.length) return fail(payload.errors[0].message);
        notify({ message: `Marked ${txnIds.length} internal.`, kind: "success" });
        afterMutation(clearSelection);
      },
      onError: () => fail("Could not mark internal — please try again."),
    });
  };

  const sendToWorklist = (txnIds: string[], clearSelection: () => void) => {
    setBusy(true);
    commitMutation<pnlCellPanel_SetTxnsNeedsReviewMutation>(environment, {
      mutation: setTxnsNeedsReviewMutation,
      variables: { input: { txnIds, needsReview: true } },
      onCompleted: (res) => {
        const payload = res.setTxnsNeedsReview;
        if (payload?.errors?.length) return fail(payload.errors[0].message);
        notify({ message: `Sent ${txnIds.length} to needs-review.`, kind: "success" });
        afterMutation(clearSelection);
      },
      onError: () => fail("Could not send to needs-review — please try again."),
    });
  };

  const exportParams: Record<string, string> = {
    pnl_line: scope.line,
    to: scope.to,
    sort: sortVars.sort,
    sort_direction: sortVars.sortDirection,
  };
  if (scope.from) exportParams.from = scope.from;
  if (scope.categoryId) exportParams.category_id = scope.categoryId;
  if (scope.counterpartyId) exportParams.counterparty_id = scope.counterpartyId;
  const perspectiveId = getPerspectiveId();
  if (perspectiveId) exportParams.perspective_id = perspectiveId;

  return (
    <TxnTablePanel
      title={scope.label}
      totalCount={data.transactions.totalCount}
      headerTotalDollars={data.transactions.totalAmount}
      rows={rows}
      emptyLabel="No transactions in this cell."
      sort={sort}
      direction={direction}
      onSort={onSort} sorting={sorting}
      exportParams={exportParams}
      busy={busy || refreshing}
      onClose={onClose}
      renderBulkActions={(selectedIds, clearSelection) => (
        <>
          <BulkAction label="Set counterparty" disabled={busy}>
            {(close) => (
              <StakeholderCombobox
                options={stakeholderOptions}
                onPick={(choice) => {
                  close();
                  attribute(selectedIds, clearSelection, choice);
                }}
              />
            )}
          </BulkAction>
          <BulkAction label="Reclassify" disabled={busy}>
            {(close) => (
              <div className="flex flex-col min-w-[16rem]">
                {RECLASSIFY_OPTIONS.map((option) => (
                  <button
                    key={option.label}
                    type="button"
                    onClick={() => {
                      close();
                      classify(selectedIds, clearSelection, option.value);
                    }}
                    className="text-left px-2 py-1.5 rounded hover:bg-gray-100 text-xs"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </BulkAction>
          <BulkAction label="Mark internal" disabled={busy}>
            {(close) => (
              <AccountCombobox
                options={accountOptions}
                placeholder="The owned account on the other end…"
                onPick={(choice) => {
                  close();
                  markInternal(selectedIds, clearSelection, choice);
                }}
              />
            )}
          </BulkAction>
          <button
            type="button"
            disabled={busy}
            onClick={() => sendToWorklist(selectedIds, clearSelection)}
            className="px-2 py-1 rounded bg-white text-gray-900 hover:bg-gray-100 text-xs disabled:opacity-50"
          >
            Send to needs-review
          </button>
        </>
      )}
    />
  );
}
